import { Account, ID, Query } from 'appwrite';
import { ApiClient } from '../../ApiClient';
import { SessionDTO, TokenDTO, LastLoginMethodDTO, UserDTO } from '../dto/AuthDTO';
import { RegisterUserDTO } from '../dto/RegisterUserDTO';
import { withOperatorContext } from '../../../application/auth/impersonation';

export class AuthDataSource {
  constructor(private api: ApiClient, private account: Account) { }

  private normalizeEmail(email: string): string {
    return email.toLowerCase().trim();
  }

  async getLastLoginMethod(email: string): Promise<LastLoginMethodDTO> {
    const response = await this.api.get<LastLoginMethodDTO>(`/users/email`, { params: { email: this.normalizeEmail(email) } });
    return response.data;
  }

  async userExists(email: string): Promise<boolean> {
    const response = await this.api.get(`/users/email`, { params: { email: this.normalizeEmail(email) } });
    return response.status === 200;
  }

  async sendCode(email: string): Promise<TokenDTO> {
    return this.account.createEmailToken(
      ID.unique(),
      this.normalizeEmail(email)
    );
  }

  async verifyCode(userId: string, code: string): Promise<SessionDTO> {
    return await this.account.createSession(
      userId,
      code
    );
  }

  async createJwt(): Promise<string> {
    const jwt = await withOperatorContext(() => this.account.createJWT());
    return jwt.jwt;
  }

  async verifyPassword(email: string, password: string): Promise<SessionDTO> {
    const response = await this.account.createEmailPasswordSession(this.normalizeEmail(email), password);
    return response as SessionDTO;
  }

  async registerUser(request: RegisterUserDTO): Promise<UserDTO> {
    const response = await this.api.post<UserDTO>('/users/register', {
      ...request,
      email: this.normalizeEmail(request.email),
    });
    return response.data;
  }

  async getCurrentSession(): Promise<SessionDTO> {
    const result = await this.account.getSession('current');
    return result as SessionDTO;
  }

  private appwriteProjectHeaders(extra?: Record<string, string>): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'X-Appwrite-Project': import.meta.env.VITE_APPWRITE_PROJECT_ID,
      ...extra,
    };
  }

  private appwriteUsersHeaders(jwt: string): Record<string, string> {
    return this.appwriteProjectHeaders({ 'X-Appwrite-JWT': jwt });
  }

  private appwriteEndpoint(): string {
    return import.meta.env.VITE_APPWRITE_URL.replace(/\/$/, '');
  }

  async getCurrentUser(): Promise<UserDTO> {
    const sdkUser = await this.account.get() as unknown as UserDTO;
    if (typeof sdkUser.impersonator === 'boolean') {
      return sdkUser;
    }

    const response = await fetch(`${this.appwriteEndpoint()}/account`, {
      method: 'GET',
      headers: this.appwriteProjectHeaders({
        'X-Appwrite-Response-Format': '1.9.0'
      }),
      credentials: 'include',
    });
    if (!response.ok) {
      return sdkUser;
    }

    const raw = await response.json() as UserDTO;
    return {
      ...sdkUser,
      impersonator: Boolean(raw.impersonator),
      impersonatorUserId: raw.impersonatorUserId || sdkUser.impersonatorUserId,
    };
  }

  async hasImpersonatorAccess(): Promise<boolean> {
    try {
      const jwt = await this.createJwt();
      const url = new URL(`${this.appwriteEndpoint()}/users`);
      url.searchParams.append('queries[]', Query.limit(1));

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.appwriteUsersHeaders(jwt),
      });

      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Lists users for the impersonation picker. Requires impersonator access on the backend.
   */
  async listUsers(search?: string, subscriptionStatuses?: string[]): Promise<UserDTO[]> {
    const response = await this.api.get<UserDTO[]>('/impersonation/users', {
      params: {
        ...(search ? { search } : {}),
        ...(subscriptionStatuses?.length ? { subscriptionStatuses: subscriptionStatuses.join(',') } : {}),
      },
    });
    return response.data;
  }

  prefetchUsers(): void {
    void this.listUsers();
  }

  async isUserLoggedIn(): Promise<boolean> {
    try {
      await this.getCurrentSession();
      return true;
    } catch {
      return false;
    }
  }

  async logout(): Promise<void> {
    await this.deleteSessions();
  }

  async deleteSessions(): Promise<void> {
    try {
      // Check if user is logged in before attempting to delete sessions
      const isLoggedIn = await this.isUserLoggedIn();
      if (!isLoggedIn) {
        // No active session to delete, return early
        return;
      }
      
      await this.account.deleteSessions();
    } catch (error) {
      // Only log non-404 errors (404 means no sessions to delete)
      if (error instanceof Error && !error.message.includes('404')) {
        console.error('Error deleting sessions:', error);
      }
    }
  }
}